import React from "react";

import {
    Document,
    Page,
    Text,
    View,
    Image,
    Svg,
    Line,
    Polygon,
    Rect,
    Circle,
    Path,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import SCHOOL_LOGO from "./savationChristLogo.png";


const CAYEP_NAME =
    "CAYEP LEADERSHIP TRAINING";

const CAYEP_SUBTITLE =
    "LEADERSHIP • DEVELOPMENT • PROFESSIONAL EXCELLENCE";

const CAYEP_ADDRESS =
    "LEADERSHIP TRAINING INSTITUTE • LIBERIA";

const CAYEP_CERTIFICATE_TITLE =
    "CERTIFICATE OF COMPLETION";

const CAYEP_CREDENTIAL =
    "PROFESSIONAL LEADERSHIP CREDENTIAL";


const COLORS = {
    navy: "#062A4B",
    blue: "#063A6B",
    black: "#0A0F12",
    gold: "#F6C944",
    yellow: "#EFA81C",
    orange: "#C0750D",
    green: "#4FAE22",
    olive: "#5F6622",
    sky: "#0F6DAB",
    white: "#F0EDEA",
    gray: "#C4C7C7",
    antique: "#CBA454",

    paper: "#FBFCFD",
    soft: "#F5F7F9",
    softBlue: "#EEF4F8",
    text: "#142536",
    muted: "#667482",
    border: "#D7E0E6",
    deepBorder: "#B8C6D0",
};


const getDisplayValue = (value) => {
    if (
        value === null ||
        value === undefined
    ) {
        return "";
    }

    if (
        typeof value === "object"
    ) {
        return (
            value.name ||
            value.title ||
            value.label ||
            value.value ||
            ""
        );
    }

    return String(value);
};


const getStudentName = (student) => {
    return (
        getDisplayValue(
            student?.full_name
        ) ||
        getDisplayValue(
            student?.name
        ) ||
        getDisplayValue(
            student?.student_name
        ) ||
        "Graduate"
    );
};


const getClassName = (gradeClass) => {
    return (
        getDisplayValue(
            gradeClass?.name
        ) ||
        "Leadership Training Program"
    );
};


const getDivision = (gradeClass) => {
    return getDisplayValue(
        gradeClass?.division
    );
};


const getAcademicYear = (
    academicYear
) => {
    return (
        getDisplayValue(
            academicYear?.name
        ) ||
        "Training Year"
    );
};


const getCertificateNumber = (
    student,
    academicYear
) => {

    const year =
        getAcademicYear(
            academicYear
        );

    const yearDigits =
        String(year)
            .replace(
                /[^0-9]/g,
                ""
            )
            .slice(-4) ||
        "2026";

    const studentId =
        student?.id ||
        student?.student_id ||
        "001";

    return `CLT-${yearDigits}-${studentId}`;
};


const styles = StyleSheet.create({

    page: {
        width: "100%",
        height: "100%",
        backgroundColor: COLORS.paper,
        fontFamily: "Helvetica",
        padding: 27,
    },

    outerFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 2,
        borderColor: COLORS.navy,
        padding: 5,
    },

    goldFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 1.5,
        borderColor: COLORS.gold,
        padding: 4,
    },

    innerFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 1,
        borderColor: COLORS.navy,
        backgroundColor: COLORS.paper,
        overflow: "hidden",
        position: "relative",
    },

    topBand: {
        width: "100%",
        height: 13,
        flexDirection: "row",
    },

    topBandGold: {
        width: "28%",
        height: 13,
        backgroundColor: COLORS.gold,
    },

    topBandBlue: {
        width: "42%",
        height: 13,
        backgroundColor: COLORS.blue,
    },

    topBandOrange: {
        width: "14%",
        height: 13,
        backgroundColor: COLORS.orange,
    },

    topBandGoldEnd: {
        width: "16%",
        height: 13,
        backgroundColor: COLORS.antique,
    },

    bottomBand: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 9,
        flexDirection: "row",
    },

    bottomNavy: {
        width: "45%",
        backgroundColor: COLORS.navy,
    },

    bottomBlue: {
        width: "30%",
        backgroundColor: COLORS.blue,
    },

    bottomGold: {
        width: "15%",
        backgroundColor: COLORS.gold,
    },

    bottomOrange: {
        width: "10%",
        backgroundColor: COLORS.orange,
    },

    cornerTopLeft: {
        position: "absolute",
        left: 0,
        top: 13,
    },

    cornerTopRight: {
        position: "absolute",
        right: 0,
        top: 13,
    },

    cornerBottomLeft: {
        position: "absolute",
        left: 0,
        bottom: 9,
    },

    cornerBottomRight: {
        position: "absolute",
        right: 0,
        bottom: 9,
    },

    content: {
        flex: 1,
        paddingHorizontal: 39,
        paddingTop: 15,
        paddingBottom: 16,
    },

    header: {
        width: "100%",
        minHeight: 61,
        flexDirection: "row",
        alignItems: "center",
    },

    logoArea: {
        width: 78,
        height: 61,
        alignItems: "flex-start",
        justifyContent: "center",
    },

    logo: {
        width: 54,
        height: 54,
        objectFit: "contain",
    },

    organizationArea: {
        flex: 1,
        paddingLeft: 3,
        paddingRight: 20,
    },

    organization: {
        color: COLORS.navy,
        fontSize: 17,
        fontWeight: "bold",
        letterSpacing: 2,
    },

    organizationSub: {
        marginTop: 4,
        color: COLORS.orange,
        fontSize: 7,
        fontWeight: "bold",
        letterSpacing: 1.15,
    },

    address: {
        marginTop: 4,
        color: COLORS.muted,
        fontSize: 6.4,
        letterSpacing: 0.65,
    },

    headerRuleArea: {
        width: 135,
        alignItems: "flex-end",
        justifyContent: "center",
    },

    headerRuleTop: {
        width: 120,
        height: 3,
        backgroundColor: COLORS.gold,
        marginBottom: 4,
    },

    headerRuleMiddle: {
        width: 96,
        height: 2,
        backgroundColor: COLORS.navy,
        marginBottom: 4,
    },

    headerRuleBottom: {
        width: 72,
        height: 2,
        backgroundColor: COLORS.orange,
    },

    headingArea: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 9,
    },

    credential: {
        color: COLORS.orange,
        fontSize: 6.7,
        fontWeight: "bold",
        letterSpacing: 2,
        textAlign: "center",
    },

    certificateTitle: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 25,
        fontWeight: "bold",
        letterSpacing: 1.5,
        textAlign: "center",
    },

    headingDecoration: {
        width: 290,
        height: 12,
        marginTop: 3,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    headingLineLeft: {
        width: 130,
        height: 1,
        backgroundColor: COLORS.antique,
    },

    headingLineRight: {
        width: 130,
        height: 1,
        backgroundColor: COLORS.antique,
    },

    headingDiamond: {
        width: 8,
        height: 8,
        marginHorizontal: 9,
        backgroundColor: COLORS.gold,
        transform: [
            {
                rotate: "45deg",
            },
        ],
    },

    recipientSection: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 2,
        paddingHorizontal: 15,
    },

    bodyIntro: {
        color: COLORS.muted,
        fontSize: 7.7,
        letterSpacing: 0.45,
        textAlign: "center",
    },

    studentName: {
        marginTop: 5,
        color: COLORS.navy,
        fontSize: 28,
        fontWeight: "bold",
        letterSpacing: 0.8,
        textAlign: "center",
        maxWidth: 575,
    },

    studentNameDecoration: {
        width: 390,
        height: 13,
        marginTop: 3,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    nameLineLeft: {
        width: 157,
        height: 1,
        backgroundColor: COLORS.antique,
    },

    nameLineRight: {
        width: 157,
        height: 1,
        backgroundColor: COLORS.antique,
    },

    nameDiamond: {
        width: 8,
        height: 8,
        marginHorizontal: 9,
        backgroundColor: COLORS.gold,
        transform: [
            {
                rotate: "45deg",
            },
        ],
    },

    recognitionArea: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 2,
    },

    recognition: {
        maxWidth: 530,
        color: COLORS.text,
        fontSize: 7.6,
        lineHeight: 1.45,
        textAlign: "center",
    },

    programSection: {
        width: "100%",
        minHeight: 53,
        marginTop: 8,
        flexDirection: "row",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.soft,
    },

    programLeft: {
        width: 7,
        backgroundColor: COLORS.navy,
    },

    programMiddle: {
        flex: 1,
        paddingVertical: 7,
        paddingHorizontal: 14,
        justifyContent: "center",
    },

    programTopLabel: {
        color: COLORS.orange,
        fontSize: 6,
        fontWeight: "bold",
        letterSpacing: 1.2,
    },

    programName: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 11,
        fontWeight: "bold",
        letterSpacing: 0.3,
    },

    division: {
        marginTop: 2,
        color: COLORS.muted,
        fontSize: 6.6,
    },

    programRight: {
        width: 128,
        borderLeftWidth: 1,
        borderLeftColor: COLORS.border,
        backgroundColor: COLORS.softBlue,
        alignItems: "center",
        justifyContent: "center",
    },

    yearLabel: {
        color: COLORS.muted,
        fontSize: 5.7,
        fontWeight: "bold",
        letterSpacing: 1,
    },

    yearValue: {
        marginTop: 4,
        color: COLORS.navy,
        fontSize: 9.5,
        fontWeight: "bold",
        letterSpacing: 0.4,
    },

    lowerArea: {
        width: "100%",
        minHeight: 48,
        marginTop: 7,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    credentialBox: {
        width: 162,
        height: 34,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.paper,
        justifyContent: "center",
        paddingHorizontal: 10,
    },

    credentialLabel: {
        color: COLORS.muted,
        fontSize: 5.2,
        letterSpacing: 0.8,
    },

    credentialValue: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 6.8,
        fontWeight: "bold",
        letterSpacing: 0.6,
    },

    badgeArea: {
        flex: 1,
        height: 48,
        alignItems: "center",
        justifyContent: "center",
    },

    badge: {
        width: 48,
        height: 48,
    },

    statusBox: {
        width: 162,
        height: 34,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.paper,
        justifyContent: "center",
        alignItems: "flex-end",
        paddingHorizontal: 10,
    },

    statusLabel: {
        color: COLORS.muted,
        fontSize: 5.2,
        letterSpacing: 0.8,
    },

    statusValue: {
        marginTop: 3,
        color: COLORS.green,
        fontSize: 6.7,
        fontWeight: "bold",
        letterSpacing: 0.5,
    },

    signatures: {
        width: "100%",
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginTop: 7,
    },

    signature: {
        width: 170,
        alignItems: "center",
    },

    signatureLine: {
        width: 145,
        borderTopWidth: 1,
        borderTopColor: COLORS.navy,
        marginBottom: 3,
    },

    signatureName: {
        color: COLORS.navy,
        fontSize: 6.3,
        fontWeight: "bold",
        letterSpacing: 0.35,
        textAlign: "center",
    },

    signatureRole: {
        marginTop: 2,
        color: COLORS.orange,
        fontSize: 5.2,
        letterSpacing: 0.45,
        textAlign: "center",
    },

    centerMark: {
        width: 145,
        alignItems: "center",
        justifyContent: "center",
    },

    centerMarkText: {
        color: COLORS.navy,
        fontSize: 5.2,
        fontWeight: "bold",
        letterSpacing: 0.85,
        textAlign: "center",
    },

    centerMarkSub: {
        marginTop: 3,
        color: COLORS.muted,
        fontSize: 4.8,
        letterSpacing: 0.5,
        textAlign: "center",
    },

    footerNote: {
        marginTop: 4,
        alignItems: "center",
    },

    footerNoteText: {
        color: COLORS.muted,
        fontSize: 4.5,
        letterSpacing: 0.65,
        textAlign: "center",
    },

});


const CornerDecoration = ({
    flipX = false,
    flipY = false,
}) => {

    const transform = [];

    if (flipX) {
        transform.push({
            scaleX: -1,
        });
    }

    if (flipY) {
        transform.push({
            scaleY: -1,
        });
    }

    return (
        <Svg
            width="105"
            height="82"
            viewBox="0 0 105 82"
            style={{
                transform,
            }}
        >

            <Polygon
                points="0,0 105,0 0,82"
                fill={COLORS.navy}
            />

            <Polygon
                points="0,0 78,0 0,60"
                fill={COLORS.blue}
            />

            <Polygon
                points="0,0 46,0 0,37"
                fill={COLORS.gold}
            />

            <Line
                x1="13"
                y1="13"
                x2="75"
                y2="13"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.28"
            />

            <Line
                x1="13"
                y1="13"
                x2="13"
                y2="62"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.22"
            />

            <Circle
                cx="16"
                cy="16"
                r="3"
                fill={COLORS.antique}
            />

        </Svg>
    );
};


const PremiumBadge = () => {

    return (
        <Svg
            width="48"
            height="48"
            viewBox="0 0 92 92"
        >

            <Circle
                cx="46"
                cy="46"
                r="43"
                fill={COLORS.navy}
            />

            <Circle
                cx="46"
                cy="46"
                r="39"
                fill="none"
                stroke={COLORS.gold}
                strokeWidth="2"
            />

            <Circle
                cx="46"
                cy="46"
                r="34"
                fill={COLORS.blue}
            />

            <Circle
                cx="46"
                cy="46"
                r="29"
                fill="none"
                stroke={COLORS.antique}
                strokeWidth="1"
            />

            <Polygon
                points="
                    46,17
                    51,31
                    66,31
                    54,40
                    58,55
                    46,47
                    34,55
                    38,40
                    26,31
                    41,31
                "
                fill={COLORS.gold}
            />

            <Circle
                cx="46"
                cy="38"
                r="9"
                fill={COLORS.navy}
            />

            <Path
                d="
                    M40 38
                    L44 42
                    L52 33
                "
                fill="none"
                stroke={COLORS.white}
                strokeWidth="2.5"
            />

            <Circle
                cx="46"
                cy="46"
                r="41.5"
                fill="none"
                stroke={COLORS.white}
                strokeWidth="0.5"
                opacity="0.3"
            />

        </Svg>
    );
};


const CertificatePage = ({
    student,
    gradeClass,
    academicYear,
}) => {

    const studentName =
        getStudentName(student);

    const className =
        getClassName(gradeClass);

    const division =
        getDivision(gradeClass);

    const year =
        getAcademicYear(
            academicYear
        );

    const certificateNumber =
        getCertificateNumber(
            student,
            academicYear
        );

    return (
        <Document>

            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
            >

                <View
                    style={styles.outerFrame}
                >

                    <View
                        style={styles.goldFrame}
                    >

                        <View
                            style={styles.innerFrame}
                        >

                            <View
                                style={styles.topBand}
                            >

                                <View
                                    style={
                                        styles.topBandGold
                                    }
                                />

                                <View
                                    style={
                                        styles.topBandBlue
                                    }
                                />

                                <View
                                    style={
                                        styles.topBandOrange
                                    }
                                />

                                <View
                                    style={
                                        styles.topBandGoldEnd
                                    }
                                />

                            </View>


                            <View
                                style={
                                    styles.cornerTopLeft
                                }
                            >
                                <CornerDecoration />
                            </View>


                            <View
                                style={
                                    styles.cornerTopRight
                                }
                            >
                                <CornerDecoration
                                    flipX
                                />
                            </View>


                            <View
                                style={
                                    styles.cornerBottomLeft
                                }
                            >
                                <CornerDecoration
                                    flipY
                                />
                            </View>


                            <View
                                style={
                                    styles.cornerBottomRight
                                }
                            >
                                <CornerDecoration
                                    flipX
                                    flipY
                                />
                            </View>


                            <View
                                style={
                                    styles.content
                                }
                            >

                                <View
                                    style={
                                        styles.header
                                    }
                                >

                                    <View
                                        style={
                                            styles.logoArea
                                        }
                                    >

                                        <Image
                                            src={
                                                SCHOOL_LOGO
                                            }
                                            style={
                                                styles.logo
                                            }
                                        />

                                    </View>


                                    <View
                                        style={
                                            styles.organizationArea
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.organization
                                            }
                                        >
                                            {CAYEP_NAME}
                                        </Text>

                                        <Text
                                            style={
                                                styles.organizationSub
                                            }
                                        >
                                            {CAYEP_SUBTITLE}
                                        </Text>

                                        <Text
                                            style={
                                                styles.address
                                            }
                                        >
                                            {CAYEP_ADDRESS}
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.headerRuleArea
                                        }
                                    >

                                        <View
                                            style={
                                                styles.headerRuleTop
                                            }
                                        />

                                        <View
                                            style={
                                                styles.headerRuleMiddle
                                            }
                                        />

                                        <View
                                            style={
                                                styles.headerRuleBottom
                                            }
                                        />

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.headingArea
                                    }
                                >

                                    <Text
                                        style={
                                            styles.credential
                                        }
                                    >
                                        {CAYEP_CREDENTIAL}
                                    </Text>

                                    <Text
                                        style={
                                            styles.certificateTitle
                                        }
                                    >
                                        {CAYEP_CERTIFICATE_TITLE}
                                    </Text>


                                    <View
                                        style={
                                            styles.headingDecoration
                                        }
                                    >

                                        <View
                                            style={
                                                styles.headingLineLeft
                                            }
                                        />

                                        <View
                                            style={
                                                styles.headingDiamond
                                            }
                                        />

                                        <View
                                            style={
                                                styles.headingLineRight
                                            }
                                        />

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.recipientSection
                                    }
                                >

                                    <Text
                                        style={
                                            styles.bodyIntro
                                        }
                                    >
                                        This credential is proudly
                                        presented to
                                    </Text>


                                    <Text
                                        style={
                                            styles.studentName
                                        }
                                    >
                                        {studentName}
                                    </Text>


                                    <View
                                        style={
                                            styles.studentNameDecoration
                                        }
                                    >

                                        <View
                                            style={
                                                styles.nameLineLeft
                                            }
                                        />

                                        <View
                                            style={
                                                styles.nameDiamond
                                            }
                                        />

                                        <View
                                            style={
                                                styles.nameLineRight
                                            }
                                        />

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.recognitionArea
                                    }
                                >

                                    <Text
                                        style={
                                            styles.recognition
                                        }
                                    >
                                        In recognition of successfully
                                        completing the prescribed
                                        leadership training requirements
                                        and demonstrating commitment to
                                        professional growth, leadership
                                        development, and the practical
                                        application of the competencies
                                        covered within the program.
                                    </Text>

                                </View>


                                <View
                                    style={
                                        styles.programSection
                                    }
                                >

                                    <View
                                        style={
                                            styles.programLeft
                                        }
                                    />


                                    <View
                                        style={
                                            styles.programMiddle
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.programTopLabel
                                            }
                                        >
                                            LEADERSHIP TRAINING PROGRAM
                                        </Text>

                                        <Text
                                            style={
                                                styles.programName
                                            }
                                        >
                                            {className}
                                        </Text>

                                        {division ? (
                                            <Text
                                                style={
                                                    styles.division
                                                }
                                            >
                                                {division}
                                            </Text>
                                        ) : null}

                                    </View>


                                    <View
                                        style={
                                            styles.programRight
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.yearLabel
                                            }
                                        >
                                            TRAINING YEAR
                                        </Text>

                                        <Text
                                            style={
                                                styles.yearValue
                                            }
                                        >
                                            {year}
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.lowerArea
                                    }
                                >

                                    <View
                                        style={
                                            styles.credentialBox
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.credentialLabel
                                            }
                                        >
                                            CERTIFICATE NUMBER
                                        </Text>

                                        <Text
                                            style={
                                                styles.credentialValue
                                            }
                                        >
                                            {certificateNumber}
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.badgeArea
                                        }
                                    >

                                        <PremiumBadge />

                                    </View>


                                    <View
                                        style={
                                            styles.statusBox
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.statusLabel
                                            }
                                        >
                                            CREDENTIAL STATUS
                                        </Text>

                                        <Text
                                            style={
                                                styles.statusValue
                                            }
                                        >
                                            OFFICIALLY COMPLETED
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.signatures
                                    }
                                >

                                    <View
                                        style={
                                            styles.signature
                                        }
                                    >

                                        <View
                                            style={
                                                styles.signatureLine
                                            }
                                        />

                                        <Text
                                            style={
                                                styles.signatureName
                                            }
                                        >
                                            AUTHORIZED DIRECTOR
                                        </Text>

                                        <Text
                                            style={
                                                styles.signatureRole
                                            }
                                        >
                                            CAYEP LEADERSHIP TRAINING
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.centerMark
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.centerMarkText
                                            }
                                        >
                                            PROFESSIONAL LEADERSHIP
                                        </Text>

                                        <Text
                                            style={
                                                styles.centerMarkText
                                            }
                                        >
                                            CREDENTIAL
                                        </Text>

                                        <Text
                                            style={
                                                styles.centerMarkSub
                                            }
                                        >
                                            Issued by CAYEP Leadership Training
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.signature
                                        }
                                    >

                                        <View
                                            style={
                                                styles.signatureLine
                                            }
                                        />

                                        <Text
                                            style={
                                                styles.signatureName
                                            }
                                        >
                                            ACADEMIC / PROGRAM REGISTRAR
                                        </Text>

                                        <Text
                                            style={
                                                styles.signatureRole
                                            }
                                        >
                                            CERTIFICATION & RECORDS
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.footerNote
                                    }
                                >

                                    <Text
                                        style={
                                            styles.footerNoteText
                                        }
                                    >
                                        CAYEP LEADERSHIP TRAINING • LEADERSHIP DEVELOPMENT • PROFESSIONAL EXCELLENCE
                                    </Text>

                                </View>

                            </View>


                            <View
                                style={
                                    styles.bottomBand
                                }
                            >

                                <View
                                    style={
                                        styles.bottomNavy
                                    }
                                />

                                <View
                                    style={
                                        styles.bottomBlue
                                    }
                                />

                                <View
                                    style={
                                        styles.bottomGold
                                    }
                                />

                                <View
                                    style={
                                        styles.bottomOrange
                                    }
                                />

                            </View>

                        </View>

                    </View>

                </View>

            </Page>

        </Document>
    );
};


const GraduateCertificatePDF = {

    downloadSingle: async (
        student,
        gradeClass,
        academicYear
    ) => {

        const blob =
            await pdf(
                <CertificatePage
                    student={student}
                    gradeClass={
                        gradeClass
                    }
                    academicYear={
                        academicYear
                    }
                />
            ).toBlob();


        const fileName =
            `${getStudentName(student)
                .replace(
                    /\s+/g,
                    "_"
                )
            }_CAYEP_Leadership_Certificate.pdf`;


        saveAs(
            blob,
            fileName
        );
    },


    downloadMultiple: async (
        students,
        gradeClass,
        academicYear
    ) => {

        const zip =
            new JSZip();


        for (
            const student of students
        ) {

            const blob =
                await pdf(
                    <CertificatePage
                        student={student}
                        gradeClass={
                            gradeClass
                        }
                        academicYear={
                            academicYear
                        }
                    />
                ).toBlob();


            const fileName =
                `${getStudentName(student)
                    .replace(
                        /\s+/g,
                        "_"
                    )
                }_CAYEP_Leadership_Certificate.pdf`;


            zip.file(
                fileName,
                blob
            );
        }


        const zipBlob =
            await zip.generateAsync({
                type: "blob",
            });


        saveAs(
            zipBlob,
            "CAYEP_Leadership_Training_Certificates.zip"
        );
    },
};


export default GraduateCertificatePDF;


